import type {
  ClockifyImportPreview,
  ClockifyMappingDecision,
  ClockifyPreviewCell,
  ClockifyPreviewGroup,
  ClockifySkipReason,
} from '@hygge/shared';

/**
 * The Clockify import screen's arithmetic and wording, with no React in it.
 *
 * It lives apart from the page for one reason: every number on that screen is a
 * claim the owner checks against Clockify before six months of other people's
 * hours are written, and a claim worth checking is worth testing. The page
 * renders; this decides.
 */

// ── Hours ────────────────────────────────────────────────────────────────────

/**
 * Seconds are the contract; hours are a formatting decision, made once, here.
 *
 * Two decimals and a thousands separator, matching what
 * `scripts/clockify-export.ts` prints — the owner is comparing two printouts
 * side by side, and a number that reads differently is a number they have to
 * re-derive before they can trust it.
 */
export function formatHours(seconds: number): string {
  return (seconds / 3600).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** The same number without grouping, for a cell that will be pasted into a spreadsheet. */
export function plainHours(seconds: number): string {
  return (seconds / 3600).toFixed(2);
}

export function formatCount(n: number): string {
  return n.toLocaleString('en-US');
}

/** `1 entry`, `2 entries`. Counting in a sentence, where "1 entries" reads as a bug in the numbers. */
export function plural(n: number, one: string, many = `${one}s`): string {
  return `${formatCount(n)} ${n === 1 ? one : many}`;
}

// ── The range ────────────────────────────────────────────────────────────────

const MONTH_RE = /^\d{4}-(0[1-9]|1[0-2])$/;

export function isMonth(value: string): boolean {
  return MONTH_RE.test(value);
}

/** Days in a month, by the calendar. Day 0 of the next month is the last day of this one. */
export function lastDayOfMonth(month: string): number {
  const [y, m] = month.split('-').map(Number);
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

/**
 * Two month pickers → the inclusive `from`/`to` the API takes.
 *
 * The end of the range is the LAST day of the closing month, not its first: a
 * range ending `2026-08-01` silently drops thirty days of August, which on this
 * data is 1,561 hours that nobody would notice missing until the migration was
 * over. Leap Februarys included — hence the calendar, not a table of 30s and 31s.
 */
export function rangeFromMonths(fromMonth: string, toMonth: string): { from: string; to: string } {
  return { from: `${fromMonth}-01`, to: `${toMonth}-${String(lastDayOfMonth(toMonth)).padStart(2, '0')}` };
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `2026-03` → `Mar 2026`. Falls back to the raw value rather than rendering "undefined 2026". */
export function monthLabel(month: string): string {
  if (!isMonth(month)) return month;
  const [y, m] = month.split('-').map(Number);
  return `${MONTH_NAMES[m - 1]} ${y}`;
}

// ── The grid ─────────────────────────────────────────────────────────────────

export interface PivotTotals {
  rows: number;
  seconds: number;
  importRows: number;
  importSeconds: number;
}

export interface PivotRow {
  id: string;
  name: string;
  /** One per month in `months`, in the same order. */
  cells: PivotTotals[];
  total: PivotTotals;
  /**
   * True for the synthetic row holding hours the month totals carry but no
   * person does. Rendered differently, because it is not a person.
   */
  unattributed?: boolean;
}

export interface Pivot {
  months: string[];
  rows: PivotRow[];
  /** Column totals, taken from the preview's own `byMonth` — see below. */
  totals: PivotTotals[];
  grand: PivotTotals;
}

const zero = (): PivotTotals => ({ rows: 0, seconds: 0, importRows: 0, importSeconds: 0 });

function add(into: PivotTotals, from: { rows: number; seconds: number; importRows: number; importSeconds: number }): void {
  into.rows += from.rows;
  into.seconds += from.seconds;
  into.importRows += from.importRows;
  into.importSeconds += from.importSeconds;
}

/**
 * Hours per person per month — the shape `scripts/clockify-export.ts` prints, so
 * the two can be read side by side.
 *
 * The column totals come from the preview's `byMonth` rather than from summing
 * the grid, and the difference is deliberate: a row with no Clockify user is
 * counted in its month but belongs to nobody, so a grid that footed its own
 * columns would quietly report a smaller month than the API did. Any such gap
 * becomes a visible "(no Clockify user)" row instead.
 */
export function buildPivot(preview: {
  byUserMonth: ClockifyPreviewCell[];
  byMonth: ClockifyPreviewGroup[];
}): Pivot {
  const months = [...new Set([...preview.byMonth.map((m) => m.id), ...preview.byUserMonth.map((c) => c.month)])].sort();
  const index = new Map(months.map((m, i) => [m, i]));

  const byPerson = new Map<string, PivotRow>();
  for (const cell of preview.byUserMonth) {
    const at = index.get(cell.month);
    if (at === undefined) continue;
    let row = byPerson.get(cell.clockifyUserId);
    if (!row) {
      row = {
        id: cell.clockifyUserId,
        name: cell.userName,
        cells: months.map(zero),
        total: zero(),
      };
      byPerson.set(cell.clockifyUserId, row);
    }
    add(row.cells[at], cell);
    add(row.total, cell);
  }

  const rows = [...byPerson.values()].sort((a, b) =>
    a.name === b.name ? a.id.localeCompare(b.id) : a.name.localeCompare(b.name),
  );

  const totals = months.map((m) => {
    const group = preview.byMonth.find((g) => g.id === m);
    const t = zero();
    if (group) add(t, group);
    return t;
  });

  // What the months carry that no person does.
  const gap: PivotRow = { id: '__unattributed__', name: '(no Clockify user)', cells: months.map(zero), total: zero(), unattributed: true };
  let anyGap = false;
  months.forEach((_, i) => {
    const summed = rows.reduce((acc, r) => acc + r.cells[i].seconds, 0);
    const summedRows = rows.reduce((acc, r) => acc + r.cells[i].rows, 0);
    const cell: PivotTotals = {
      rows: totals[i].rows - summedRows,
      seconds: totals[i].seconds - summed,
      importRows: totals[i].importRows - rows.reduce((acc, r) => acc + r.cells[i].importRows, 0),
      importSeconds: totals[i].importSeconds - rows.reduce((acc, r) => acc + r.cells[i].importSeconds, 0),
    };
    if (cell.rows !== 0 || cell.seconds !== 0) anyGap = true;
    gap.cells[i] = cell;
    add(gap.total, cell);
  });

  const grand = zero();
  for (const t of totals) add(grand, t);

  return { months, rows: anyGap ? [...rows, gap] : rows, totals, grand };
}

/**
 * The grid as tab-separated text.
 *
 * Tabs rather than a pretty table: this is pasted into a spreadsheet next to
 * Clockify's own export and subtracted. Hours are printed ungrouped for the same
 * reason — "16,733.91" is text to a spreadsheet, "16733.91" is a number.
 */
export function pivotToTsv(pivot: Pivot, meta: { from: string; to: string; timeZone: string; source: string }): string {
  const lines: string[] = [];
  lines.push(`Clockify hours per person per month\t${meta.from} to ${meta.to}\tbucketed in ${meta.timeZone}\tsource: ${meta.source}`);
  lines.push(['person', ...pivot.months, 'TOTAL'].join('\t'));
  for (const row of pivot.rows) {
    lines.push([row.name, ...row.cells.map((c) => plainHours(c.seconds)), plainHours(row.total.seconds)].join('\t'));
  }
  lines.push(['TOTAL hours', ...pivot.totals.map((t) => plainHours(t.seconds)), plainHours(pivot.grand.seconds)].join('\t'));
  lines.push(['entries', ...pivot.totals.map((t) => String(t.rows)), String(pivot.grand.rows)].join('\t'));
  return lines.join('\n');
}

// ── Exclusions ───────────────────────────────────────────────────────────────

/**
 * Why a row is not being written, in the words a person would use.
 *
 * Every reason the contract defines has an entry: a reason that rendered as a
 * raw enum would be the one thing on the page nobody could act on.
 */
export const SKIP_REASON_LABELS: Record<ClockifySkipReason, string> = {
  project_unmapped: 'Clockify project not mapped yet',
  project_skipped: 'Clockify project deliberately skipped',
  user_unmapped: 'Person not mapped yet',
  user_skipped: 'Person deliberately skipped',
  already_imported: 'Already imported by an earlier run',
  no_project: 'Entry has no Clockify project',
  no_user: 'Entry has no Clockify user',
  non_positive_duration: 'Duration is zero or negative',
  invalid_duration: 'Duration is not a whole number of seconds',
  invalid_interval: 'Start time missing or unreadable',
  interval_mismatch: 'Duration contradicts its own start and end',
  duplicate_row: 'Duplicate of an entry already in this batch',
  no_external_id: 'Entry has no Clockify id to key on',
  external_id_too_long: 'Clockify id too long for the column it has to fit',
  running_timer: 'Timer was still running at export time',
};

export function skipReasonLabel(reason: ClockifySkipReason | string): string {
  return SKIP_REASON_LABELS[reason as ClockifySkipReason] ?? reason;
}

/** The two reasons that are a question for the owner rather than a fact about the data. */
export function isBlockingReason(reason: ClockifySkipReason | string): boolean {
  return reason === 'project_unmapped' || reason === 'user_unmapped';
}

// ── Mapping decisions ────────────────────────────────────────────────────────

/** Sentinel for the "skip" choice in a dropdown whose other options are ids. */
export const SKIP_OPTION = '__skip__';
/** Sentinel for "no decision yet". Never sent as a decision — see `decisionOf`. */
export const PENDING_OPTION = '';

export interface DecisionCounts {
  total: number;
  mapped: number;
  skipped: number;
  pending: number;
}

export function countDecisions(rows: Array<{ decision: ClockifyMappingDecision }>): DecisionCounts {
  return {
    total: rows.length,
    mapped: rows.filter((r) => r.decision === 'map').length,
    skipped: rows.filter((r) => r.decision === 'skip').length,
    pending: rows.filter((r) => r.decision === 'pending').length,
  };
}

/**
 * One dropdown value → the decision the API takes.
 *
 * The three states stay three all the way to the wire. Collapsing "not decided"
 * into "skip" on the way out would turn an unanswered question into a silent
 * discarding of hours, which is the single failure this screen exists to
 * prevent.
 */
export function decisionOf(value: string): { decision: ClockifyMappingDecision; target: string | null } {
  if (value === SKIP_OPTION) return { decision: 'skip', target: null };
  if (value === PENDING_OPTION) return { decision: 'pending', target: null };
  return { decision: 'map', target: value };
}

/** …and back, to seed the dropdown from what the server says is in force. */
export function valueOf(mapping: { decision: ClockifyMappingDecision; target: string | null }): string {
  if (mapping.decision === 'skip') return SKIP_OPTION;
  if (mapping.decision === 'map' && mapping.target) return mapping.target;
  return PENDING_OPTION;
}

// ── The sentence on the confirm dialog ───────────────────────────────────────

/**
 * What the button is about to do, restated in full.
 *
 * Deliberately the preview's own numbers rather than a recount: the point of the
 * confirmation is that the figure the owner approved is the figure that gets
 * written, and a second derivation of it would be a second chance to disagree.
 */
export function describeImport(preview: ClockifyImportPreview): string {
  return (
    `Write ${plural(preview.rowsToImport, 'entry', 'entries')} — ${formatHours(preview.secondsToImport)} hours — for ` +
    `${plural(preview.employeesAffected, 'person', 'people')} across ` +
    `${plural(preview.projectsAffected, 'project')}, ` +
    `covering ${preview.from} to ${preview.to}.`
  );
}

/**
 * Why the Import button is disabled, or null when it is not.
 *
 * The button's own reason, in one line, so it never sits greyed out with no
 * explanation — and derived from `canImport`, which the server computed, so the
 * screen cannot come to a different conclusion than the endpoint would.
 */
export function importBlockedReason(preview: ClockifyImportPreview | null): string | null {
  if (!preview) return 'Scan the range first — nothing has been read yet.';
  if (preview.canImport) return null;
  const outstanding = preview.unmappedProjects.length + preview.unmappedUsers.length;
  if (outstanding > 0) {
    const parts: string[] = [];
    if (preview.unmappedProjects.length) parts.push(plural(preview.unmappedProjects.length, 'project'));
    if (preview.unmappedUsers.length) parts.push(plural(preview.unmappedUsers.length, 'person', 'people'));
    return `${parts.join(' and ')} still ${outstanding === 1 ? 'needs' : 'need'} a decision.`;
  }
  const already = preview.skipped.find((s) => s.reason === 'already_imported');
  if (already && already.rows > 0) {
    const done = `Every entry in this range is already imported — ${formatCount(already.rows)} of them.`;
    // The trap this sentence exists for: a decision corrected AFTER an import
    // is a no-op. The mapping table shows the new target, the rows still sit on
    // the old one, and "everything is already imported" is true in a way that
    // reads as "the correction is in".
    if (preview.mappingDrift.rows > 0) {
      const moved = [...preview.mappingDrift.projects, ...preview.mappingDrift.users].map((g) => g.name);
      return (
        `${done} ${formatCount(preview.mappingDrift.rows)} of them sit somewhere the mapping no longer says ` +
        `(${moved.slice(0, 3).join(', ')}${moved.length > 3 ? ', …' : ''}) — changing a decision does not move hours ` +
        'that are already written. Undo the run below and import the range again.'
      );
    }
    return done;
  }
  return 'Nothing in this range would be written.';
}
